import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateUserGroupRelatedQueries } from '@/modules/common/utils/query-invalidation';

import { setDefaultUserGroup } from '../../api/user-groups.api';

/**
 * Marks a group as the organization's default.
 *
 * `name` is carried purely for the toast, matching the convention in the delete hook.
 *
 * Refetched rather than patched optimistically: the flip clears the marker from one group and sets
 * it on another, and only the server knows which group held it. The same invalidation is what moves
 * the "(Default)" suffix in the invite dropdown.
 */
export const useSetDefaultUserGroup = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.settings');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: ({
      id,
    }: {
      id: number;
      /** Only used for the success toast — the endpoint takes just the id */
      name: string;
    }) => setDefaultUserGroup({ id }),
    onSuccess: async (_data, variables) => {
      await invalidateUserGroupRelatedQueries(queryClient);
      displayToastSuccess(
        t('pages.permissions.toasts.groupSetAsDefault', { name: variables.name })
      );
      onSuccess?.();
    },
    onError: (error: unknown) =>
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.settings:api.error.userGroups.setDefaultFailed'
      ),
  });
};
